import getScene from '@cartwhl/common/api/getScene';
import postExternalScene from '@cartwhl/common/api/postExternalScene';
import postExternalSceneReference from '@cartwhl/common/api/postExternalSceneReference';
import type { SceneSettings } from '@cartwhl/common/types';
import {
  FACE_EXPRESSIONS,
  type FrameRate,
  HAND_OPTIONS,
  SceneType,
  frameRates,
} from '@cartwhl/common/types';
import type { Scene, SceneTimelineUpdateItem } from '@cartwhl/common/types';

import { userStore } from 'stores/global';

import { DEFAULT_CHARACTER, PATH, PLAYER_FRAME_RATE } from '../constants';
import type { MotionEditorScene } from '../types';
import { createMotionEditorSceneFromScene } from './scenePlaybackUtils';

export type MotionEditorSceneCreateProgress = 'creating-scene';

const MAX_SCENE_NAME_LENGTH = 100;
const SCENE_EDIT_SUFFIX = ' edit';

type MotionEditorSceneSettingsPayload = Pick<
  SceneSettings,
  | 'backgroundSettings'
  | 'cameraSettings'
  | 'characterSettings'
  | 'labelSettings'
  | 'playbackSettings'
>;

export type MotionEditorSceneSettingsPlaybackOverrides = {
  frameRate?: number;
  frameStepSize?: number;
};

function toSupportedSceneFrameRate(frameRate: number | undefined): FrameRate {
  if (frameRate != null && frameRates.includes(frameRate as FrameRate)) {
    return frameRate as FrameRate;
  }

  return PLAYER_FRAME_RATE;
}

export function getMotionEditorSceneNameForJob(
  jobTitle: string | undefined,
  jobId: string,
): string {
  const defaultSceneName = jobTitle?.trim() || `Scene from ${jobId}`;
  const maxBaseNameLength = MAX_SCENE_NAME_LENGTH - SCENE_EDIT_SUFFIX.length;
  return `${defaultSceneName.slice(0, maxBaseNameLength)}${SCENE_EDIT_SUFFIX}`;
}

export function getMotionEditorCreateScenePath(
  motionID: string,
  sceneName?: string,
  outputCount?: number,
): string {
  const params = new URLSearchParams();
  const trimmedName = sceneName?.trim();
  if (trimmedName) {
    params.set('name', trimmedName);
  }
  if (outputCount && outputCount > 1) {
    params.set('outputs', String(outputCount));
  }
  const query = params.toString();
  return `${PATH}/create/${encodeURIComponent(motionID)}${query ? `?${query}` : ''}`;
}

interface CreateMotionEditorSceneFromExistingMotionOptions {
  apiKey: string;
  motionID: string;
  existingVariationNumber?: number;
  outputCount?: number;
  /** Zero-based output / body index for comic multi-person jobs. Defaults to `0`. */
  outputNumber?: number;
  sceneName?: string;
  /** Overrides default Axel when opening from a custom character (e.g. Cartographer). */
  characterID?: string;
  onProgress?: (progress: MotionEditorSceneCreateProgress) => void;
}

function sceneTimelineItemToUpdateItem(
  item: Scene['objects'][string]['timeline'][number],
): SceneTimelineUpdateItem {
  return {
    appliedMotionEditJobID: item.appliedMotionEditJobID,
    jobID: item.jobID,
    title: item.title,
    trimEnd: item.trimEnd,
    trimStart: item.trimStart,
    variationNumber: item.variationNumber,
  };
}

/**
 * Scene create always stamps the default character. After create, swap each
 * scene reference to `characterID` via POST /scenes/{id}/{reference}.
 */
async function updateCreatedSceneCharacter({
  apiKey,
  sceneID,
  characterID,
}: {
  apiKey: string;
  sceneID: string;
  characterID: string;
}): Promise<void> {
  const { data: scene, error: loadError } = await getScene(sceneID, apiKey);
  if (loadError || !scene) {
    throw loadError ?? new Error('Failed to load created scene.');
  }

  const sceneObjectEntries = Object.entries(scene.objects ?? {});
  if (sceneObjectEntries.length === 0) {
    throw new Error('Created scene has no character references to update.');
  }

  for (const [referenceName, sceneObject] of sceneObjectEntries) {
    if (sceneObject.objectID === characterID) {
      continue;
    }

    const { error } = await postExternalSceneReference(
      apiKey,
      sceneID,
      referenceName,
      {
        objectID: characterID,
        stitchFromIndex: 0,
        timeline: sceneObject.timeline.map(sceneTimelineItemToUpdateItem),
      },
    );

    if (error) {
      throw error;
    }
  }
}

export async function createMotionEditorSceneIdFromMotion({
  apiKey,
  motionID,
  existingVariationNumber = 0,
  outputCount = 1,
  outputNumber = 0,
  sceneName,
  characterID,
  onProgress,
}: CreateMotionEditorSceneFromExistingMotionOptions): Promise<string> {
  onProgress?.('creating-scene');
  const motionCount = Math.max(1, Math.floor(outputCount));
  const startOutputNumber = Math.max(0, Math.floor(outputNumber));
  const trimmedCharacterID = characterID?.trim();
  const normalizedSceneName = (sceneName?.trim() || 'Untitled scene').slice(
    0,
    MAX_SCENE_NAME_LENGTH,
  );

  const { data: createdScene, error: createError } = await postExternalScene(
    apiKey,
    {
      sceneName: normalizedSceneName,
      motions: Array.from({ length: motionCount }, (_, index) => ({
        motionID,
        outputNumber: startOutputNumber + index,
        variationNumber: existingVariationNumber,
      })),
      // Create with default Axel; character swap happens after create when needed.
      sceneSettings: getDefaultMotionEditorSceneSettings(),
      type: SceneType.MOTION,
      createdByUser: userStore.state.userInfo?.userID,
    },
  );

  if (createError) {
    throw createError;
  }

  if (!createdScene?.sceneID) {
    throw new Error('Failed to create scene.');
  }

  // Create always stamps Axel; swap references afterward when a custom character is requested.
  if (trimmedCharacterID && trimmedCharacterID !== DEFAULT_CHARACTER.jobID) {
    await updateCreatedSceneCharacter({
      apiKey,
      sceneID: createdScene.sceneID,
      characterID: trimmedCharacterID,
    });
  }

  return createdScene.sceneID;
}

export async function createMotionEditorSceneFromExistingMotion(
  options: CreateMotionEditorSceneFromExistingMotionOptions,
): Promise<MotionEditorScene> {
  const sceneID = await createMotionEditorSceneIdFromMotion(options);
  const { data, error } = await getScene(sceneID, options.apiKey);
  if (error || !data) {
    throw error ?? new Error('Failed to load created scene.');
  }

  return createMotionEditorSceneFromScene(data);
}

export function getDefaultMotionEditorSceneReferenceName() {
  return `${DEFAULT_CHARACTER.jobID}_0`;
}

export function getDefaultMotionEditorSceneSettings(
  playbackOverrides?: MotionEditorSceneSettingsPlaybackOverrides,
): MotionEditorSceneSettingsPayload {
  const settings: MotionEditorSceneSettingsPayload = {
    backgroundSettings: {
      enableShadows: true,
      hexColor: '#42aad7',
      gridCellSize: 1,
      showGrid: false,
      showGroundPlane: false,
      useBackgroundGradient: true,
      version: '1.0.0',
    },
    cameraSettings: {
      aspectRatio: -1,
      cameraFollow: false,
      serializedCamera: '',
      version: '1.0.0',
    },
    characterSettings: {
      charUploadID: DEFAULT_CHARACTER.jobID,
      rootPosition: [0, 0.5, 0],
      rootRotation: [0, 0, 0],
      faceExpression: FACE_EXPRESSIONS.DEFAULT,
      skinHex: 'default',
      staticHands: HAND_OPTIONS.DEFAULT,
      showSkeleton: false,
      enableRootMotion: true,
      version: '1.0.0',
    },
    labelSettings: {
      text: '',
      size: 8,
      marginTop: 5,
      marginLeft: 5,
      marginRight: 5,
      color: '#000000',
      style: 'style-1',
      alignment: 'center',
      layer: 'back',
      version: '1.0.0',
    },
    playbackSettings: {
      frameRate: toSupportedSceneFrameRate(playbackOverrides?.frameRate),
      frameStepSize: playbackOverrides?.frameStepSize ?? 1,
      playbackSpeed: 1,
      version: '1.0.0',
    },
  };

  return settings;
}
